/**
 * Single source of truth for "does this canvas object belong in a saved garden?"
 *
 * The editor puts a lot of scaffolding on the Fabric canvas that is pure UI:
 * the grid, snap guides, wizard geometry, placement ghosts, the drawing tools'
 * in-progress markers, the dashed group edit-mode indicator, and the draggable
 * vertex handles of polygon/bezier edit mode.
 *
 * All three persistence paths must agree on that set (#683). They did not:
 * `serializeCanvasObjects` (backend) filtered a strict subset of what
 * `saveCanvas` (localStorage) filtered, so a save that happened to run while
 * vertex edit mode was active — the 30s auto-save, or the flush when leaving
 * the editor (#680) — wrote the live vertex handles into the garden. They came
 * back on the next load as inert circles frozen at their old positions: visible,
 * selectable, without any listener, and one more generation on every cycle.
 *
 * The predicate reads nothing but `data`, so it works both on live Fabric
 * objects and on their serialized JSON — which is what lets the load path use
 * it to heal gardens that already carry such leftovers.
 */

/** `data.type` values that only ever describe editor scaffolding. */
const NON_PERSISTABLE_TYPES = new Set([
  'wizard-background',
  'garden-boundary',
  'height-zone',
  'height-zone-label',
  'vertex-handle',
]);

/**
 * True when an object is user content that belongs in the saved garden.
 *
 * @param {{data?: object}} obj - Live Fabric object or serialized object JSON
 * @returns {boolean}
 */
export function isPersistableObject(obj) {
  if (!obj) return false;

  const data = obj.data;
  if (!data) return true; // No metadata at all — treat as user content

  if (
    data.isGridLine ||
    data.isSnapGuide ||
    data.isEditModeIndicator ||
    data.isGhost ||
    data.isTemporary
  ) {
    return false;
  }

  return !NON_PERSISTABLE_TYPES.has(data.type);
}
